import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiCopy, FiDownload, FiSearch } from "react-icons/fi";
import {
  AUTH_NOTE,
  BASE_URLS,
  MUNSHOT_DATA_APIS,
  REGISTRY_INTRO,
  buildFullMarkdownDoc,
  getApiHost,
} from "../data/munshotDataApis";
import MunshotApiCard, { methodBadgeStyle, useCopy } from "./MunshotApiCard";
import {
  GuideCloseButton,
  titleStyle,
  introStyle,
  calloutStyle,
  listStyle,
} from "./dashboardSkillGuideCommon";

const secondaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  height: "40px",
  padding: "0 14px",
  borderRadius: "6px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#eef2ff",
  color: "#3730a3",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const primaryCopyStyle = {
  ...secondaryButtonStyle,
  backgroundColor: "#3730a3",
  borderColor: "#3730a3",
  color: "#ffffff",
};

const toolbarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "16px",
  alignItems: "center",
};

const searchWrapperStyle = {
  position: "relative",
  flex: "1 1 280px",
  minWidth: "220px",
  maxWidth: "420px",
};

const searchInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px 10px 36px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  fontSize: "14px",
  color: "#1e293b",
};

const searchIconStyle = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6366f1",
  pointerEvents: "none",
};

const suggestionListStyle = {
  position: "absolute",
  left: 0,
  right: 0,
  top: "calc(100% + 4px)",
  zIndex: 20,
  margin: 0,
  padding: "6px 0",
  listStyle: "none",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
  maxHeight: "280px",
  overflowY: "auto",
};

const suggestionButtonStyle = {
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "transparent",
  padding: "10px 14px",
  cursor: "pointer",
  fontSize: "13px",
  color: "#1e293b",
};

const matchesApiSearch = (api, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const host = getApiHost(api);
  const haystack = [
    api.id,
    api.name,
    api.shortDescription,
    api.method,
    api.path,
    api.service,
    host,
    ...(api.requestFields || []).flatMap((f) => [f.name, f.description]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const MunshotDataApiDocs = ({ onClose, initialApiId = null }) => {
  const { copiedKey, copyText } = useCopy();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const fullMd = buildFullMarkdownDoc();

  const filteredApis = useMemo(
    () => MUNSHOT_DATA_APIS.filter((api) => matchesApiSearch(api, searchQuery)),
    [searchQuery]
  );

  const navigateToApi = useCallback((apiId) => {
    const el = document.getElementById(apiId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.style.outline = "2px solid #6366f1";
      el.style.outlineOffset = "4px";
      setTimeout(() => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      }, 1600);
    }
    setSearchQuery("");
    setSearchFocused(false);
  }, []);

  // Opened from a doc callout (e.g. Sheets → Run API docs): jump to that endpoint.
  useEffect(() => {
    if (initialApiId) navigateToApi(initialApiId);
  }, [initialApiId, navigateToApi]);

  const downloadMarkdown = useCallback(() => {
    const blob = new Blob([fullMd], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "munshot-data-api-docs.md";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [fullMd]);

  const showSuggestions = searchFocused && searchQuery.trim().length > 0;

  return (
    <div>
      <GuideCloseButton onClose={onClose} />

      <h2 style={titleStyle}>Munshot Datasource Registry — API Docs</h2>
      <p style={introStyle}>{REGISTRY_INTRO}</p>

      <div style={calloutStyle}>
        <strong>Auth:</strong> {AUTH_NOTE}
        <br />
        <br />
        <strong>Base URLs:</strong> <code>fastapi</code> → {BASE_URLS.fastapi} · <code>nestjs</code> →{" "}
        {BASE_URLS.nestjs}
      </div>

      <div style={toolbarStyle}>
        <div style={searchWrapperStyle}>
          <FiSearch style={searchIconStyle} aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filteredApis.length > 0) {
                e.preventDefault();
                navigateToApi(filteredApis[0].id);
              }
            }}
            placeholder="Search APIs by name, id, path…"
            style={searchInputStyle}
            aria-label="Search Munshot APIs"
            aria-autocomplete="list"
          />
          {showSuggestions && (
            <ul style={suggestionListStyle} role="listbox">
              {filteredApis.length === 0 ? (
                <li style={{ padding: "10px 14px", fontSize: "13px", color: "#64748b" }}>No APIs match.</li>
              ) : (
                filteredApis.map((api) => (
                  <li key={api.id} role="option">
                    <button
                      type="button"
                      style={suggestionButtonStyle}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => navigateToApi(api.id)}
                    >
                      <span style={methodBadgeStyle(api.method)}>{api.method}</span>
                      {api.name}{" "}
                      <span style={{ color: "#64748b" }}>
                        <code>{api.id}</code>
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        <button type="button" style={primaryCopyStyle} onClick={() => copyText("all", fullMd)}>
          {copiedKey === "all" ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
          {copiedKey === "all" ? "Copied full markdown" : "Copy all endpoints as markdown"}
        </button>
        <button type="button" style={secondaryButtonStyle} onClick={downloadMarkdown}>
          <FiDownload aria-hidden="true" />
          Download .md file
        </button>
      </div>

      <ul style={{ ...listStyle, marginBottom: "24px", columns: "2 280px" }}>
        {filteredApis.map((api) => (
          <li key={api.id} style={{ breakInside: "avoid" }}>
            <a
              href={`#${api.id}`}
              style={{ color: "#3730a3" }}
              onClick={(e) => {
                e.preventDefault();
                navigateToApi(api.id);
              }}
            >
              {api.method} {api.name}
            </a>
          </li>
        ))}
      </ul>

      {filteredApis.length === 0 ? (
        <p style={introStyle}>No APIs match “{searchQuery.trim()}”.</p>
      ) : (
        filteredApis.map((api) => (
          <MunshotApiCard key={api.id} api={api} copiedKey={copiedKey} copyText={copyText} />
        ))
      )}
    </div>
  );
};

export default MunshotDataApiDocs;
